import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Navigation } from './components/Navigation';
import { Footer } from './components/Footer';
import { PageSEO } from './components/PageSEO';
import { OrganizationSchema, WebSiteSchema, InvestmentFundSchema } from './components/StructuredData';
// Lazy-loaded page components for faster initial load
const Home = lazy(() => import('./pages/Home').then(m => ({
  default: m.Home
})));
const Approach = lazy(() => import('./pages/Approach').then(m => ({
  default: m.Approach
})));
const Portfolio = lazy(() => import('./pages/Portfolio').then(m => ({
  default: m.Portfolio
})));
const Founders = lazy(() => import('./pages/Founders').then(m => ({
  default: m.Founders
})));
const Team = lazy(() => import('./pages/Team').then(m => ({
  default: m.Team
})));
const Values = lazy(() => import('./pages/Values').then(m => ({
  default: m.Values
})));
const Insights = lazy(() => import('./pages/Insights').then(m => ({
  default: m.Insights
})));
const Stories = lazy(() => import('./pages/Stories').then(m => ({
  default: m.Stories
})));
const DeepDiveReader = lazy(() => import('./pages/DeepDiveReader').then(m => ({
  default: m.DeepDiveReader
})));
const Contact = lazy(() => import('./pages/Contact').then(m => ({
  default: m.Contact
})));
const seoConfig: Record<string, {
  title: string;
  description: string;
  keywords: string;
  ogType?: string;
}> = {
  '/': {
    title: 'Vitality Venture Fund | Biomimicry-Driven Venture Capital',
    description: "Vitality is a venture fund investing in deep tech and hard tech startups rooted in biomimicry. We fuel nature-inspired innovation by applying 3.8 billion years of nature's R&D combined with modern AI and technology across agriculture, construction, and materials.",
    keywords: 'biomimicry venture capital, nature-inspired investing, deep tech fund, hard tech startups, regenerative agriculture investment, climate tech VC, bio-based materials, circular economy fund'
  },
  '/approach': {
    title: 'Our Approach — Biomimicry Investment Thesis | Vitality Venture Fund',
    description: "Vitality uses a biomimicry-based investment filter to identify pre-seed and seed stage startups creating essential solutions. We invest across three themes — Grow (agriculture), Build (construction), and Use (materials) — applying nature's 3.8 billion years of R&D.",
    keywords: 'biomimicry investment thesis, nature-inspired startups, pre-seed venture fund, seed stage investing, regenerative agriculture, bio-based construction, circular materials, deep tech investment strategy'
  },
  '/portfolio': {
    title: 'Portfolio — Nature-Inspired Startups | Vitality Venture Fund',
    description: "Explore Vitality's portfolio of biomimicry-driven startups: BOPA (autonomous nano-tractors for regenerative agriculture), TierraSphere (AI for soil regeneration), ReefCycle (nature-inspired cement), and Mothership Materials (containerized biomanufacturing).",
    keywords: 'BOPA precision ag, TierraSphere soil AI, ReefCycle cement, Mothership Materials biomanufacturing, biomimicry startups, nature-inspired solutions portfolio, regenerative agriculture startups'
  },
  '/founders': {
    title: 'Natalya Wallin — Founder & GP | Vitality Venture Fund',
    description: 'Meet Natalya Wallin, GP & Managing Director of Vitality Venture Fund. A high-conviction leader with 10+ years deploying a $75M global fund, backgrounds in biology and molecular genetics, Executive MBA from Duke, and Moab 240-mile ultramarathon finisher.',
    keywords: 'Natalya Wallin, Vitality founder, venture capital GP, biomimicry investor, ultramarathon entrepreneur, Duke MBA, molecular genetics, women in venture capital',
    ogType: 'profile'
  },
  '/team': {
    title: 'Team — Multidisciplinary Experts in Nature-Inspired Innovation | Vitality',
    description: 'Meet the Vitality team: Natalya Wallin (GP), Patrick Livingston (Built Environment), Rollin Nash (Operations), plus advisors and an Investment Review Committee with expertise spanning engineering, biology, climate finance, and fund management.',
    keywords: 'Vitality team, venture capital team, biomimicry experts, built environment investing, operations specialist, investment review committee, climate finance advisors'
  },
  '/values': {
    title: 'Values — Conviction, Athleticism, Adventure, Connection | Vitality',
    description: "Vitality's four core values: Conviction to fuel nature-inspired startups, Athleticism to navigate unknowns with grit and discipline, Adventure to explore bold ventures with curiosity, and Connection across disciplines, sectors, and industries.",
    keywords: 'venture capital values, conviction investing, nature-inspired innovation values, biomimicry fund culture, interdisciplinary collaboration'
  },
  '/insights': {
    title: 'Insights — Press, Deep Thinking & Data | Vitality Venture Fund',
    description: 'Latest from Vitality: press, events, deep dives into nature-inspired innovation, and our Substack writings on biomimicry and regenerative systems.',
    keywords: 'Vitality news, biomimicry venture capital press, nature-inspired investing insights, regenerative technology blog, deep tech data visualization'
  },
  '/stories': {
    title: 'Stories — A Living Record of a Mission in Motion | Vitality Venture Fund',
    description: "This is what it looks like to build at the intersection of nature's intelligence and the most important moment in the history of innovation. We tell the stories the capital system hasn't gotten to yet.",
    keywords: 'Vitality stories, biomimicry venture capital narrative, nature-inspired investing journal, regenerative technology essays, venture capital field notes'
  },
  '/contact': {
    title: 'Contact Vitality Venture Fund — Founders & Investors Welcome',
    description: "Connect with Vitality Venture Fund. Whether you're a founder building nature-inspired solutions, an investor aligned with biomimicry-driven innovation, or a partner in regenerative technology, we'd love to hear from you.",
    keywords: 'contact Vitality, biomimicry venture capital contact, nature-inspired startup funding, venture fund inquiry, Natalya Wallin contact'
  }
};
// SEO manager that updates meta tags on route change
function SEOManager() {
  const {
    pathname
  } = useLocation();
  const seo = seoConfig[pathname] || seoConfig['/'];
  return <PageSEO title={seo.title} description={seo.description} path={pathname} keywords={seo.keywords} ogType={seo.ogType} data-id="element-0" />;
}
// Scroll to top on route change
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
// Minimal loading fallback for code-split routes
function PageLoader() {
  return <div className="min-h-screen flex items-center justify-center" data-id="element-1">
      <div className="w-8 h-8 border-2 border-vitalityTeal/30 border-t-vitalityTeal rounded-full animate-spin" data-id="element-2" />
    </div>;
}
export function App() {
  return <Router data-id="element-3">
      <div className="flex flex-col min-h-screen bg-deepCanopy text-white font-sans selection:bg-vitalityTeal selection:text-deepCanopy" data-id="element-4">
        <ScrollToTop data-id="element-5" />
        <SEOManager data-id="element-6" />
        <OrganizationSchema data-id="element-7" />
        <WebSiteSchema data-id="element-8" />
        <InvestmentFundSchema data-id="element-9" />
        <Navigation data-id="element-10" />
        <main className="flex-grow" data-id="element-11">
          <Suspense fallback={<PageLoader data-id="element-13" />} data-id="element-12">
            <Routes data-id="element-14">
              <Route path="/" element={<Home data-id="element-16" />} data-id="element-15" />
              <Route path="/approach" element={<Approach data-id="element-18" />} data-id="element-17" />
              <Route path="/portfolio" element={<Portfolio data-id="element-20" />} data-id="element-19" />
              <Route path="/founders" element={<Founders data-id="element-22" />} data-id="element-21" />
              <Route path="/team" element={<Team data-id="element-24" />} data-id="element-23" />
              <Route path="/values" element={<Values data-id="element-26" />} data-id="element-25" />
              <Route path="/insights" element={<Insights data-id="element-28" />} data-id="element-27" />
              <Route path="/stories" element={<Stories data-id="element-30" />} data-id="element-29" />
              <Route path="/insights/deep-dives/:slug" element={<DeepDiveReader data-id="element-32" />} data-id="element-31" />
              <Route path="/contact" element={<Contact data-id="element-34" />} data-id="element-33" />
            </Routes>
          </Suspense>
        </main>
        <Footer data-id="element-35" />
      </div>
    </Router>;
}